import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Plus, Edit, UserCog, Archive, Trash2, ChevronRight, LayoutGrid, LayoutList } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DatabaseEmployee } from '@/types/employee';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { OrgChart } from './organization/OrgChart';

export const EmployeeList = () => {
  const [employees, setEmployees] = useState<DatabaseEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveStatus, setArchiveStatus] = useState<'archived' | 'terminated' | 'resigned'>('archived');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'org'>('list');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employee_profiles')
        .select('*')
        .order('last_name', { ascending: sortOrder === 'asc' });

      if (error) throw error;
      setEmployees(data as unknown as DatabaseEmployee[]);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = () => {
    navigate('/employee/new');
  };

  const handleEditEmployee = (id: string) => {
    navigate(`/employee/edit/${id}`);
  };

  const handleManageReporting = (id: string) => {
    navigate(`/employee/reporting/${id}`);
  };

  const handleViewEmployee = (id: string) => {
    navigate(`/employee/profile/${id}`);
  };

  const handleArchiveEmployee = async () => {
    if (!selectedEmployeeId || !archiveReason) return;

    try {
      const { error } = await supabase
        .from('employee_profiles')
        .update({
          status: archiveStatus,
          archived_at: new Date().toISOString(),
          archived_reason: archiveReason
        })
        .eq('id', selectedEmployeeId);

      if (error) throw error;

      toast.success('Employee archived successfully');
      setArchiveReason('');
      setArchiveStatus('archived');
      setSelectedEmployeeId(null);
      fetchEmployees();
    } catch (error) {
      console.error('Error archiving employee:', error);
      toast.error('Failed to archive employee');
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployeeId) return;

    try {
      const { error } = await supabase
        .from('employee_profiles')
        .delete()
        .eq('id', selectedEmployeeId);

      if (error) throw error;

      toast.success('Employee deleted successfully');
      setSelectedEmployeeId(null);
      setShowDeleteDialog(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Failed to delete employee');
    }
  };

  const toggleSortOrder = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    const sortedEmployees = [...employees].sort((a, b) => {
      const comparison = a.last_name.localeCompare(b.last_name);
      return newOrder === 'asc' ? comparison : -comparison;
    });
    setEmployees(sortedEmployees);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Employees</h2>
        <div className="flex gap-2">
          <div className="flex gap-2 border rounded-lg p-1 bg-muted">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <LayoutList className="h-4 w-4 mr-2" />
              List
            </Button>
            <Button
              variant={viewMode === 'org' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('org')}
            >
              <LayoutGrid className="h-4 w-4 mr-2" />
              Org Chart
            </Button>
          </div>
          {viewMode === 'list' && (
            <Button onClick={toggleSortOrder} size="sm" variant="outline">
              Sort {sortOrder === 'asc' ? '↓' : '↑'}
            </Button>
          )}
          <Button onClick={() => setEditMode(!editMode)} size="sm" variant="outline">
            <Edit className="h-4 w-4 mr-2" />
            {editMode ? 'Exit Edit Mode' : 'Edit Mode'}
          </Button>
          <Button onClick={handleAddEmployee} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Employee
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((employee) => (
            <Card 
              key={employee.id} 
              className={`hover:shadow-lg transition-shadow cursor-pointer ${
                editMode ? 'ring-2 ring-primary/10' : ''
              }`}
              onClick={() => !editMode && handleViewEmployee(employee.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-12 w-12">
                    {employee.avatar_url ? (
                      <AvatarImage 
                        src={employee.avatar_url} 
                        alt={`${employee.first_name} ${employee.last_name}`}
                        className="object-cover"
                        onError={(e) => {
                          console.error('Avatar load error:', e);
                          e.currentTarget.src = '';
                        }}
                      />
                    ) : (
                      <AvatarFallback className="bg-primary/10">
                        {employee.first_name?.[0]}{employee.last_name?.[0]}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1">
                    <h3 className="font-medium">{employee.first_name} {employee.last_name}</h3>
                    <p className="text-sm text-muted-foreground">{employee.title}</p>
                    <p className="text-sm text-muted-foreground">{employee.department}</p>
                    {employee.status !== 'active' && (
                      <p className="text-sm text-orange-600 font-medium capitalize">{employee.status}</p>
                    )}
                  </div>
                  {!editMode ? (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <div className="flex space-x-2">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEmployee(employee.id);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleManageReporting(employee.id);
                        }}
                      >
                        <UserCog className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEmployeeId(employee.id);
                            }}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Archive Employee</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to archive this employee? This action can be reversed by an administrator.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="archiveStatus">Status</Label>
                              <Select
                                value={archiveStatus}
                                onValueChange={(value: 'archived' | 'terminated' | 'resigned') => 
                                  setArchiveStatus(value)
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="archived">Archived</SelectItem>
                                  <SelectItem value="terminated">Terminated</SelectItem>
                                  <SelectItem value="resigned">Resigned</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="archiveReason">Reason</Label>
                              <Textarea
                                id="archiveReason"
                                value={archiveReason}
                                onChange={(e) => setArchiveReason(e.target.value)}
                                placeholder="Please provide a reason..."
                                className="min-h-[100px]"
                              />
                            </div>
                          </div>
                          <AlertDialogFooter>
                            <AlertDialogCancel onClick={() => {
                              setArchiveReason('');
                              setArchiveStatus('archived');
                              setSelectedEmployeeId(null);
                            }}>
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleArchiveEmployee}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Archive Employee
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      
                      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEmployeeId(employee.id);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Employee</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to permanently delete this employee? This action cannot be undone.
                              Consider archiving the employee instead to maintain historical records.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel onClick={() => {
                              setSelectedEmployeeId(null);
                              setShowDeleteDialog(false);
                            }}>
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDeleteEmployee}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete Employee
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <OrgChart />
      )}
    </div>
  );
};